import React, { useState, useEffect } from 'react';
import './SlideshowEditor.css';

const API_BASE = '';

function SlideshowEditor({ photos, authToken, onUpdate, onClose, slideshowSettings }) {
  const [localPhotos, setLocalPhotos] = useState([...photos]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedPhotoId, setDraggedPhotoId] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [isRandomMode, setIsRandomMode] = useState(slideshowSettings?.random || false);
  const PHOTOS_PER_PAGE = 12;

  const totalPages = Math.ceil(localPhotos.length / PHOTOS_PER_PAGE);
  const startIdx = currentPage * PHOTOS_PER_PAGE;
  const displayedPhotos = localPhotos.slice(startIdx, startIdx + PHOTOS_PER_PAGE);

  const handleDragStart = (e, index) => {
    if (isRandomMode) {
      e.preventDefault();
      alert('Random mode is enabled! Disable random mode to manually reorder photos.');
      return;
    }
    const actualIndex = startIdx + index;
    const photo = localPhotos[actualIndex];
    setDraggedItem(actualIndex);
    setDraggedPhotoId(photo.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    const targetIndex = startIdx + index;
    
    if (draggedPhotoId === null) return;
    
    // Find current position of dragged photo
    const currentDraggedIndex = localPhotos.findIndex(p => p.id === draggedPhotoId);
    if (currentDraggedIndex === -1 || currentDraggedIndex === targetIndex) return;
    
    // Reorder items
    const newPhotos = [...localPhotos];
    const draggedPhoto = newPhotos[currentDraggedIndex];
    
    // Remove from current position
    newPhotos.splice(currentDraggedIndex, 1);
    
    // Insert at target position
    newPhotos.splice(targetIndex, 0, draggedPhoto);
    
    // Update state
    setLocalPhotos(newPhotos);
    setDraggedItem(targetIndex);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDragEnd = (e) => {
    setDraggedItem(null);
    setDraggedPhotoId(null);
    
    // Save the final order to backend
    savePhotoOrder();
  };

  const handleDrop = (e) => {
    e.preventDefault();
  };

  const savePhotoOrder = async () => {
    const photo_orders = localPhotos.map((photo, idx) => {
      photo.order = idx;
      return { id: photo.id, order: idx };
    });
    
    try {
      await fetch(`${API_BASE}/api/admin/photos/reorder`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Basic ${authToken}`
        },
        body: JSON.stringify({ photo_orders })
      });
    } catch (error) {
      console.error('Failed to save photo order:', error);
    }
  };

  const toggleRandomMode = async () => {
    const newMode = !isRandomMode;
    setIsRandomMode(newMode);
    
    // Save random mode setting
    try {
      await fetch(`${API_BASE}/api/admin/slideshow-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ random: newMode })
      });
      
      if (newMode) {
        // Shuffle photos when enabling random mode
        const shuffled = [...localPhotos];
        
        // Fisher-Yates shuffle with no consecutive repeats
        for (let i = shuffled.length - 1; i > 0; i--) {
          let j;
          do {
            j = Math.floor(Math.random() * (i + 1));
          } while (i > 0 && i < shuffled.length - 1 && 
                   shuffled[j].id === shuffled[i - 1].id);
          
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        
        setLocalPhotos(shuffled);
        alert('Random mode enabled! Photos will display in random order.');
      } else {
        // Re-sort by order when disabling random mode
        const sorted = [...localPhotos].sort((a, b) => a.order - b.order);
        setLocalPhotos(sorted);
        alert('Random mode disabled. You can now manually reorder photos.');
      }
    } catch (error) {
      console.error('Failed to save slideshow settings');
    }
  };


  const handlePhotoUpload = async (e) => {
    e.preventDefault(); // Prevent any form submission
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('caption', '');
        formData.append('order', localPhotos.length);
        
        const response = await fetch(`${API_BASE}/api/admin/photos`, {
          method: 'POST',
          headers: {
            'Authorization': `Basic ${authToken}`
          },
          body: formData
        });
        
        if (response.ok) {
          const newPhoto = await response.json();
          localPhotos.push(newPhoto.photo);
          successCount++;
        } else {
          const errorText = await response.text();
          console.error('Upload failed for', file.name, 'Status:', response.status, 'Error:', errorText);
          failCount++;
          // Show error for debugging
          if (failCount === 1) { // Only show error once
            alert(`Failed to upload ${file.name}: ${response.status} - ${errorText}`);
          }
        }
      } catch (error) {
        console.error('Upload error for', file.name, error);
        failCount++;
      }
    }
    
    setLocalPhotos([...localPhotos]);
    if (onUpdate) onUpdate();
    
    // No alerts - just upload silently
    
    setUploading(false);
  };

  const handleDeletePhoto = async (photoId) => {
    // No confirmation - just delete
    try {
      const response = await fetch(`${API_BASE}/api/admin/photos/${photoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${authToken}`
        }
      });
      
      // Remove from local state immediately regardless of response
      setLocalPhotos(localPhotos.filter(p => p.id !== photoId));
      if (onUpdate) onUpdate();
      
      // No alerts at all
    } catch (error) {
      console.error('Delete error:', error);
      // Still remove from UI even if backend fails
      setLocalPhotos(localPhotos.filter(p => p.id !== photoId));
      if (onUpdate) onUpdate();
    }
  };

  const handleCaptionEdit = async (photoId, newCaption) => {
    try {
      await fetch(`${API_BASE}/api/admin/photos/${photoId}/caption`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ caption: newCaption })
      });
      
      setLocalPhotos(localPhotos.map(p => 
        p.id === photoId ? { ...p, caption: newCaption } : p
      ));
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to update caption');
    }
  };

  return (
    <div className="slideshow-editor-modal">
      <div className="slideshow-editor">
        <div className="editor-header">
          <h2>Slideshow Editor</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="editor-toolbar">
          <div className="toolbar-left">
            <label className="upload-btn" onClick={(e) => e.stopPropagation()}>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                disabled={uploading}
                onClick={(e) => e.stopPropagation()}
              />
              {uploading ? 'Uploading...' : '➕ Add Photos'}
            </label>
            <button 
              className={`btn-random ${isRandomMode ? 'active' : ''}`} 
              onClick={toggleRandomMode}
            >
              🎲 {isRandomMode ? 'Disable Random' : 'Enable Random'}
            </button>
          </div>
          <div className="toolbar-right">
            {isRandomMode && (
              <span className="random-indicator">
                🎲 RANDOM MODE ACTIVE
              </span>
            )}
            <span className="photo-count">
              {localPhotos.length} photos
            </span>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button 
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
            >
              ← Previous
            </button>
            <span>Page {currentPage + 1} of {totalPages}</span>
            <button 
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage === totalPages - 1}
            >
              Next →
            </button>
          </div>
        )}

        <div 
          className="photos-grid"
        >
          {displayedPhotos.map((photo, index) => {
            const isBeingDragged = draggedPhotoId === photo.id;
            
            return (
              <div
                key={photo.id}
                className={`photo-tile ${isRandomMode ? 'no-drag' : ''} ${isBeingDragged ? 'dragging' : ''}`}
                draggable={!isRandomMode}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnter={(e) => handleDragEnter(e, index)}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
              >
              <div className="photo-number">#{startIdx + index + 1}</div>
              <img 
                src={`${API_BASE}/uploads/${photo.filename}`} 
                alt={photo.caption || `Photo ${startIdx + index + 1}`}
              />
              <div className="photo-controls">
                <textarea
                  className="caption-input"
                  placeholder="Add caption..."
                  value={photo.caption || ''}
                  onChange={(e) => {
                    const newCaption = e.target.value;
                    setLocalPhotos(localPhotos.map(p => 
                      p.id === photo.id ? { ...p, caption: newCaption } : p
                    ));
                    // Auto-resize textarea
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  onBlur={(e) => handleCaptionEdit(photo.id, e.target.value)}
                  onFocus={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                />
                <button 
                  className="delete-photo-btn"
                  onClick={() => handleDeletePhoto(photo.id)}
                  title="Delete photo"
                >
                  🗑️
                </button>
              </div>
            </div>
            );
          })}
        </div>

        <div className="editor-footer">
          <div className="instructions">
            {isRandomMode ? (
              <>
                <p>🎲 Random mode is active - photos will display in random order</p>
                <p>🚫 Drag and drop is disabled in random mode</p>
                <p>📌 Disable random mode to manually reorder photos</p>
              </>
            ) : (
              <>
                <p>📌 Drag and drop photos to reorder them</p>
                <p>➕ Click "Add Photos" to upload new images</p>
                <p>💾 Don't forget to save your order changes!</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SlideshowEditor;